import { HttpParams } from '@angular/common/http'
import { Component, computed, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faFireFlameCurved, faSearch } from '@fortawesome/free-solid-svg-icons'
import { CollectionView } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import {
    AllowDragging,
    AllowSorting,
    CellEditEndingEventArgs,
    CellRangeEventArgs,
    CellType,
    Column,
    DataMap,
    FlexGrid,
    GridPanel,
    HeadersVisibility,
    SelectionMode,
} from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    FeatureType,
    Grid,
    JobOwnership,
    JobOwnershipJob,
    ListItem,
    ModalBackDrop,
    ModalSize,
    VpJob,
    VpUser,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { AddJobOwnershipTemplateComponent } from './add-job-ownership-template.component'
import { AddJobComponent } from './add-job.component'
import { JobOwnershipComponent } from './job-ownership.component'

@Component({
    templateUrl: './job-ownerships.component.html',
    styleUrls: ['./job-ownerships.component.css'],
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        ActionToolbarButtonsComponent,
        FaIconComponent,
    ],
})
export class JobOwnershipsComponent extends BaseComponent {
    faSearch = faSearch
    faFire = faFireFlameCurved
    itemIds: any[] = []
    active = 0
    jobGrid: Grid<JobOwnershipJob>
    grid: Grid<JobOwnership>
    myItemsGrid: Grid<JobOwnership>

    allJobs: VpJob[] = []
    jobMap: ListItem<string>[] = []
    ownershipJobs: any[] = []
    selectedJob = signal('')
    hideJobRelatedButtons = computed(() => this.selectedJob() === 'add' || this.selectedJob() === 'all' || this.selectedJob() === '')
    hideNonJobRelatedButtons = computed(() => this.selectedJob() === 'add' || this.selectedJob() === '')
    taskNameMap: ListItem<string>[] = []
    selectedTaskName = signal('')

    allUserMap!: DataMap
    activeUserMap!: DataMap
    statusMap!: DataMap
    availableAgendas: ListItem<number>[] = [
        new ListItem(0, 'Kickoff Agenda'),
        new ListItem(1, 'Quarterly Agenda'),
        new ListItem(2, 'Closeout Agenda'),
        new ListItem(3, 'Monthly Agenda'),
        new ListItem(4, 'Weekly Agenda'),
    ]
    selectedAgenda = 0

    jobStatusList: ListItem<number>[] = [
        new ListItem(1, 'Active'),
        new ListItem(2, 'Soft Closed'),
        new ListItem(3, 'Hard Closed'),
        new ListItem(0, 'Pending'),
    ]
    jobStatusMap: DataMap = new DataMap(this.jobStatusList, 'value', 'display')

    constructor() {
        super()
        this.jobGrid = new Grid<JobOwnershipJob>(this.services)

        this.jobGrid.doubleClick.on(this.jobGridDoubleClick.bind(this))

        this.grid = new Grid<JobOwnership>(this.services)
            .setupModalEditing(
                this.services.data.jobOwnerships,
                'Job Ownership',
                JobOwnershipComponent,
                () => new JobOwnership(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.name])
            .isReadOnly(false) // We need to default this to false, but the control edits more granularly.
            .editOnDoubleClick(false)
            .allowDragging(AllowDragging.Both)
            .selectionMode(SelectionMode.MultiRange)

        this.myItemsGrid = new Grid<JobOwnership>(this.services)
            .isReadOnly(false) // We need to default this to false, but the control edits more granularly.
            .editOnDoubleClick(false)

        const reordered = () => {
            this.ownershipGridReordered()
        }
        this.grid.dragged.on(reordered)

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            'refresh',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAllByAction('GetActiveAndSoftClose'),
                    this.services.data.jobOwnershipTemplates.queryAll(),
                    this.services.data.jobOwnerships.queryAll(),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                )
                .subscribe({
                    next: result => {
                        this.allJobs = result[0] as VpJob[]
                        const existingJobs = result[2]
                            .orderByDescending(x => x.job)
                            .orderBy(x => x.jobStatus)
                            .map(x => x.job)
                            .distinct()
                        const jobMap = [] as any[]
                        for (const existingJob of existingJobs) {
                            const match = this.allJobs.find(x => x.id == existingJob)
                            if (match) {
                                jobMap.push(new ListItem<string>(match.id, match.id + ' ' + match.name))
                            }
                        }
                        jobMap.unshift(new ListItem<string>('all', 'Show Ownerships from All Jobs'))
                        jobMap.unshift(new ListItem<string>('add', 'Add Job from Template'))
                        jobMap.unshift(new ListItem<string>('', 'Select a Job'))
                        this.jobMap = jobMap

                        // We want the grid to show a value for any user, even inactive ones.
                        this.allUserMap = new DataMap(result[3] as VpUser[], 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeUserMap = new DataMap(result[4] as VpUser[], 'id', 'name')

                        const statusMap = [new ListItem(0, 'On Track'), new ListItem(1, 'Off Track'), new ListItem(2, 'Done')]
                        this.statusMap = new DataMap(statusMap, 'value', 'display')
                        this.refreshOwnerships()
                    },
                }),
        )
    }

    refreshOwnerships() {
        this.cancelableRequest(
            'refreshOwnerships',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.jobOwnerships.queryAll(),
                    this.services.data.get('api/JobOwnership/GetMyItems'), // Don't use 'queryByAction' or it will overwrite our ownership repo
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                        this.myItemsGrid.items = result[1]

                        if (this.selectedJob() === 'all') {
                            this.updateTaskNameMap()
                        }

                        this.applyFilters()
                        this.updateJobSummaries()
                    },
                }),
        )
    }

    updateJobSummaries() {
        const today = new Date()
        const groupedByJob = this.grid.items.toObjectGroups(
            x => x.job,
            x => x,
        )
        const jojs = [] as JobOwnershipJob[]
        for (const prop in groupedByJob) {
            const ownerships = groupedByJob[prop]
            const joj = new JobOwnershipJob()
            joj.job = ownerships[0].job
            joj.jobFullName = ownerships[0].job + ' ' + ownerships[0].jobFullName
            joj.jobStatus = ownerships[0].jobStatus
            joj.hotCount = ownerships.filter(x => x.isHot).length
            joj.offTrackCount = ownerships.filter(x => x.status == 1).length
            joj.lateCount = ownerships.filter(x => x.dueDate && x.dueDate <= today && !x.doneDate).length
            joj.dueSoonCount = ownerships.filter(x => x.dueDate && x.dueDate <= today.addDays(7) && x.dueDate > today && !x.doneDate).length
            joj.pmUserId = this.allJobs.find(x => prop == x.id)?.pmUserId
            joj.siteSuperUserId = this.allJobs.find(x => prop == x.id)?.siteSuperUserId
            joj.peUserId = this.allJobs.find(x => prop == x.id)?.peUserId
            joj.jcaUserId = this.allJobs.find(x => prop == x.id)?.jcaUserId
            jojs.push(joj)
        }
        this.jobGrid.items = jojs.orderByDescending(x => x.job).orderBy(x => x.jobStatus)
    }

    jobGridDoubleClick(data) {
        console.log(data.hitTest.row)
        const item = this.jobGrid.flexGrid.rows[data.hitTest.row].dataItem
        this.selectedJob.set(item.job)
        this.jobChanged()
        this.active = 1
    }

    jobChanged() {
        if (this.selectedJob() === 'add') {
            this.openAddJobModal()
        } else {
            // Update task name map when "all" is selected, clear it otherwise
            if (this.selectedJob() === 'all') {
                this.updateTaskNameMap()
            } else {
                this.selectedTaskName.set('')
                this.taskNameMap = []
            }

            this.applyFilters()
            this.grid.refresh()
            this.grid.flexGrid.allowSorting = AllowSorting.MultiColumn
            if (this.selectedJob() !== 'all' && this.selectedJob() !== 'add') {
                console.log('specific job')
                this.grid.flexGrid.headersVisibility = HeadersVisibility.All
                this.grid.flexGrid.allowDragging = AllowDragging.Both
            } else {
                this.grid.flexGrid.headersVisibility = HeadersVisibility.Column
                this.grid.flexGrid.allowDragging = AllowDragging.Columns
            }

            if (!this.canEdit) {
                this.grid.flexGrid.headersVisibility = HeadersVisibility.Column
                this.grid.flexGrid.allowDragging = AllowDragging.Columns
            }

            this.refreshOwnershipsGridLayout()
        }
    }

    filterOwnerships() {
        this.grid.flexGridFilter?.clear()
        this.grid.filter = ''
    }

    ownershipGridReordered() {
        const ownerships = this.grid.view.items
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.put('/api/JobOwnership/BulkUpdate', ownerships)).subscribe(
                _successResult => {
                    this.refreshOwnerships()
                },
                _errorResult => {
                    // Error handling
                },
            ),
        )
    }

    openAddJobModal() {
        const ref = this.services.modals.open(AddJobComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as AddJobComponent
        instance.parentComponent = this

        ref.result.then(
            saveResult => {
                this.refreshOwnerships()

                const match = this.allJobs.find(x => x.id == saveResult)
                if (match) this.jobMap.push(new ListItem(match.id, match.id + ' ' + match.name))
                this.selectedJob.set(saveResult)
                this.grid.refresh()
            },
            _errorResult => {
                // Error handling
            },
        )
    }

    openAddJobOwnershipTemplateModal() {
        const ref = this.services.modals.open(AddJobOwnershipTemplateComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as AddJobOwnershipTemplateComponent
        instance.selectedJob = this.selectedJob()
        ref.result.then(
            _saveResult => {
                this.refreshOwnerships()
            },
            _errorResult => {
                // Error handling
            },
        )
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column data map to just active users, so inactive users cannot be selected.
        const col = flexGrid.columns[args.col] as Column
        if (col.binding == 'assignedUserId') col.dataMap = this.activeUserMap
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const dataItem = flexGrid.rows[args.row].dataItem
        const col = flexGrid.columns[args.col]

        // Set the column mapping back to all users, so inactive user names still appear.
        if (col.binding == 'assignedUserId') col.dataMap = this.allUserMap

        if (args.cancel) return

        // When status is set to done, and there is no done date, set the done date to now.
        if (col.binding == 'status' && args.data != 2 && dataItem.status == 2 && !dataItem.doneDate) {
            dataItem.doneDate = new Date()
            flexGrid.refresh()
        }
        this.updateOwnerships([flexGrid.rows[args.row].dataItem])
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        const row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (let currentRow = row; currentRow <= row2; currentRow++) {
            const dataItem = flexGrid.rows[currentRow].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        this.itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => this.itemIds.indexOf(x.id) > -1)
        this.updateOwnerships(itemsToSave)
        this.itemIds = [] // clear immediately and not on promise fulfillment.
    }

    updateOwnerships(items: JobOwnership[]) {
        this.updateJobSummaries()
        this.services.data.put('api/JobOwnership/BulkUpdate', items).subscribe({
            next: () => {
                // Success handling
            },
            error: () => {
                this.services.notification.error('The ownership changes were not successfully saved')
            },
        })
    }

    tabChanged() {
        if (this.active === 1 || this.active === 2) {
            this.refreshOwnerships()
        }
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 0)
    }

    private refreshOwnershipsGridLayout() {
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 0)
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 100)
    }

    updateTaskNameMap() {
        const uniqueTaskNames = this.grid.items
            .map(x => x.name)
            .filter((name, index, self) => name && self.indexOf(name) === index)
            .sort()
        this.taskNameMap = uniqueTaskNames.map(name => new ListItem<string>(name, name))
        if (this.taskNameMap.length > 0) {
            const hasPlaceholder = this.taskNameMap.some(x => x.value === '')
            if (!hasPlaceholder) {
                this.taskNameMap.unshift(new ListItem<string>('', 'Select a Task Name'))
            }
        }
    }

    applyFilters() {
        const view = this.grid.view as CollectionView
        // Clear existing filters (keep only the first one if it exists from filterBy)
        if (view._filters.length > 1) {
            view._filters.splice(1, 100)
        } else if (view._filters.length === 1) {
            view._filters.length = 0
        }

        // Apply job filter (show all jobs when "all" is selected, or filter by specific job)
        if (this.selectedJob() && this.selectedJob() !== 'add') {
            if (this.selectedJob() === 'all') {
                // When "all" is selected, show all items (no job filter)
                // But apply task name filter if one is selected
                if (this.selectedTaskName()) {
                    view._filters.push(x => x.name === this.selectedTaskName())
                }
            } else {
                // Filter by specific job
                view._filters.push(x => x.job === this.selectedJob())
            }
        }
    }

    taskNameChanged() {
        this.applyFilters()
        this.grid.refresh()
    }

    toggleHot(cell) {
        if (this.canEdit) {
            cell.item.isHot = !cell.item.isHot
            this.grid.refresh()
            this.updateOwnerships([cell.item])
        } else {
            this.services.notification.info('You do not have permissions to change this field')
        }
    }

    mainGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.ColumnHeader) {
            // Empty block
        }
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c] as any
            const rowItem = row.dataItem

            if (col.binding == 'isHot') {
                cell.style.color = rowItem.isHot ? 'red' : 'lightgray'
                if (rowItem.isHot) {
                    cell.children[0].children[0].children[0].classList.add('fa-beat-fade')
                } else {
                    cell.children[0].children[0].children[0].classList.remove('fa-beat-fade')
                }
            }
        }
    }

    // For this view, we have two edit permission levels:
    // canEdit: this is for admins to edit anything.
    // canEditProject: PMs, PEs, and SiteSupers can edit anything in their own projects.
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsJobOwnershipsEdit])
    }

    get canEditProject() {
        if (this.canEdit) return true
        if (!this.selectedJob()) return false
        const job = this.allJobs.find(x => x.id == this.selectedJob())
        if (!job) return false
        return (
            job.pmUserId == this.services.authentication.hrref ||
            job.peUserId == this.services.authentication.hrref ||
            job.siteSuperUserId == this.services.authentication.hrref
        )
    }
}
